import MarketingNavbar from '@/components/marketing/MarketingNavbar';

/**
 * Minimal honest privacy stub linked from every transactional email
 * footer. The footer link exists to satisfy SendGrid account review
 * + CAN-SPAM compliance; a fuller policy will be drafted before the
 * customer-facing launch. Content covers data collected, how it's
 * used, third parties involved, contact path, and last-updated date.
 */
export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-[#0c0907] text-white">
      <MarketingNavbar />
      <main className="mx-auto max-w-3xl px-6 py-16">
        <h1 className="text-3xl font-semibold tracking-tight md:text-4xl">
          Privacy Policy
        </h1>
        <p className="mt-2 text-sm text-white/50">Last updated: June 11, 2026</p>

        <section className="mt-8 space-y-4 text-sm leading-relaxed text-white/80">
          <p>
            RekPlay (operated by RekPlay Platform Inc.) provides an automated
            recreation-facility booking and access platform. This policy
            describes what data we collect, why, who it is shared with, and how
            to contact us about it. We collect only what is necessary to
            operate the platform.
          </p>

          <h2 className="pt-4 text-lg font-semibold text-white">
            Data We Collect
          </h2>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Account information: name, email address, and (optionally)
              phone number, locale preference, and password hash.
            </li>
            <li>
              Booking history: facility, sport, session times, access codes,
              add-ons, and operator-side notes.
            </li>
            <li>
              Payment information: handled by Stripe. We store only the last
              four digits and the Stripe payment-method reference, never the
              full card number.
            </li>
            <li>
              Technical data: IP address, user-agent, and basic request logs
              for security and abuse prevention.
            </li>
          </ul>

          <h2 className="pt-4 text-lg font-semibold text-white">
            How We Use It
          </h2>
          <ul className="list-disc space-y-1 pl-5">
            <li>To operate the booking and access-control system.</li>
            <li>
              To send transactional notifications you would expect from a
              booking platform (confirmations, reminders, receipts,
              cancellations). You can manage notification preferences in your
              account.
            </li>
            <li>To investigate abuse, fraud, or operational incidents.</li>
            <li>To meet legal and tax obligations.</li>
          </ul>

          <h2 className="pt-4 text-lg font-semibold text-white">
            Who We Share It With
          </h2>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <strong>Stripe</strong> — for payment processing and payout to
              operators. See Stripe's own privacy policy.
            </li>
            <li>
              <strong>SendGrid</strong> — for sending transactional emails.
            </li>
            <li>
              <strong>Twilio</strong> — for sending SMS notifications and
              phone-number verification.
            </li>
            <li>
              <strong>The operator</strong> running the facility you booked —
              they see your name, contact details, and booking history at
              their facility so they can operate it.
            </li>
            <li>
              We do not sell personal data and do not share it with marketing
              partners.
            </li>
          </ul>

          <h2 className="pt-4 text-lg font-semibold text-white">
            Data Requests &amp; Contact
          </h2>
          <p>
            To request a copy of your data, correct it, or delete your
            account, email{' '}
            <a
              href="mailto:privacy@rekplay.io"
              className="text-[#C2713A] underline"
            >
              privacy@rekplay.io
            </a>{' '}
            from the address on file. We respond within 30 days.
          </p>

          <h2 className="pt-4 text-lg font-semibold text-white">Updates</h2>
          <p>
            We will revise this page when we make material changes. Continued
            use of the platform after a revision indicates acceptance of the
            updated policy.
          </p>
        </section>
      </main>
    </div>
  );
}
